import React, { useEffect, useRef, useState } from "react";
import EnhancedAIAgentV2 from "../../components/EnhancedAIAgentV2";
import SystemInput from "../../components/SystemInput";
import { V2_ROUTES } from "../state/useV2Navigation";
import { useV2Theme } from "../theme/ThemeProvider";
import { useV2EnrichmentReview } from "../state/V2EnrichmentReviewContext";
import { userCanContribute, userIsAdministrator } from "../../utils/userRoles";
import {
  clearKnowledgePrefill,
  clearOpenAddPanel,
  consumeOpenAddPanel,
} from "../../utils/knowledgePrefill";
import { consumeAgentPrefill } from "../../utils/agentPrefill";
import { navigateHowto } from "../../utils/adminDeepLink";
import { v2cx } from "../theme/tokens";
import { useProductGuide } from "../guide/GuideContext";
import { emitGuideEvent } from "../guide/guideEvents";
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import { useMobileChrome } from "../mobile/MobileChromeContext";
import { isDemoSession } from "../demo/demoMode";
import SoftGateSheet from "../components/SoftGateSheet";

const OPEN_ASK_KEY = "dp_open_ask_panel";

function consumeOpenAskPanel() {
  try {
    const flag = sessionStorage.getItem(OPEN_ASK_KEY);
    sessionStorage.removeItem(OPEN_ASK_KEY);
    return flag === "1";
  } catch {
    return false;
  }
}

function redirectAddToMap(onNavigate) {
  try {
    sessionStorage.setItem("dp_open_add_from_map", "1");
  } catch {
    /* ignore */
  }
  if (typeof onNavigate === "function") {
    onNavigate(V2_ROUTES.architectureGallery);
  }
}

const HOW_BUILT_STEPS = [
  { title: "Source", body: "Customer Payments Hub notes and banking documents are the input." },
  { title: "AI proposes", body: "Applications, interfaces, and technology stream in as proposals." },
  { title: "You steward", body: "Approve, link, or skip in Inbox — the map becomes the system of record." },
  { title: "Ask works", body: "Answers are grounded in that curated map, with evidence." },
];

/** Landing workspace: Ask only (Add knowledge lives in nav). */
const V2HomePage = ({
  onNavigate,
  route = "",
  isActive = true,
  onGateAuthenticated,
  onGateNeedsGoogle,
  onGateFullSignup,
}) => {
  const { tokens } = useV2Theme();
  const { isMobile } = useDeviceDetection();
  const { setChrome } = useMobileChrome();
  const [panel, setPanel] = useState("ask");
  const [showHowBuilt, setShowHowBuilt] = useState(false);
  const [showSoftGate, setShowSoftGate] = useState(false);
  const [askLaunch, setAskLaunch] = useState(null);
  const [addPanelKey, setAddPanelKey] = useState(0);
  const agentRef = useRef(null);
  const productGuide = useProductGuide();

  useEffect(() => {
    if (!isActive) return;
    emitGuideEvent("home-panel", { panel });
  }, [isActive, panel]);

  const { hasPendingEnrichmentReview, pendingReviewCount } =
    useV2EnrichmentReview();

  // Re-read on each render so /me hydration and role fixes are visible
  // without requiring a full remount (empty useMemo froze Viewer gates).
  let user = {};
  try {
    user = JSON.parse(localStorage.getItem("user") || "{}") || {};
  } catch {
    user = {};
  }

  const displayName =
    (typeof user?.name === "string" && user.name.trim()) ||
    (typeof user?.email === "string" && user.email.split("@")[0]) ||
    null;

  const isDemo = isDemoSession();

  const canContribute = userCanContribute(user);

  useEffect(() => {
    if (!isActive) return undefined;
    if (consumeOpenAddPanel()) {
      clearOpenAddPanel();
      if (!canContribute) {
        return undefined;
      }
      if (typeof onNavigate === "function") {
        onNavigate(V2_ROUTES.addKnowledge);
      } else {
        setPanel("add");
      }
      return undefined;
    }
    if (consumeOpenAskPanel()) {
      const prefill = consumeAgentPrefill();
      if (prefill?.query) {
        setAskLaunch({
          query: prefill.query,
          entity: prefill.entity || null,
        });
      } else {
        setAskLaunch(null);
      }
      setPanel("ask");
    }
    return undefined;
  }, [canContribute, isActive, isMobile, onNavigate, route]);

  useEffect(() => {
    const onOpen = (ev) => {
      const panelName = String(ev?.detail?.panel || "").trim();
      if (panelName === "add") {
        if (!canContribute) return;
        if (typeof onNavigate === "function") {
          onNavigate(V2_ROUTES.addKnowledge);
          return;
        }
        setAddPanelKey((n) => n + 1);
        setPanel("add");
      } else if (panelName === "ask") {
        const prefill = consumeAgentPrefill();
        if (prefill?.query) {
          setAskLaunch({
            query: prefill.query,
            entity: prefill.entity || null,
          });
        }
        setPanel("ask");
      }
    };
    window.addEventListener("dp-open-home-panel", onOpen);
    return () => window.removeEventListener("dp-open-home-panel", onOpen);
  }, [canContribute, isMobile, onNavigate]);

  const openAddKnowledge = () => {
    if (!canContribute) return;
    if (
      productGuide?.active &&
      productGuide?.chapter?.id === "intent" &&
      typeof productGuide.beginOnAddKnowledge === "function"
    ) {
      productGuide.beginOnAddKnowledge();
      return;
    }
    clearKnowledgePrefill();
    if (typeof onNavigate === "function") {
      onNavigate(V2_ROUTES.addKnowledge);
      return;
    }
    setAddPanelKey((n) => n + 1);
    setPanel("add");
  };

  const handleBack = () => setPanel("ask");

  const showAsk = panel !== "add";

  // Mobile shell: Ask title + New chat + ⋯ (history). Re-assert after paint so
  // the shell's route baseline (which clears trailing) cannot win the race.
  // Skip while Home is kept mounted but hidden (another menu is active).
  useEffect(() => {
    if (!isMobile || !isActive) return undefined;
    let cancelled = false;
    const apply = () => {
      if (cancelled) return;
      if (panel === "add") {
        setChrome({
          chromeMode: "tabs",
          contextTitle: "Add knowledge",
          onBack: () => setPanel("ask"),
          backLabel: "Ask",
          headerTrailing: null,
          actionTray: null,
          hideShellHeader: false,
        });
        return;
      }
      // Ask chrome (panel may be "ask" or briefly "hub" while still showing Ask)
      setChrome({
        chromeMode: "tabs",
        contextTitle: "Ask",
        onBack: null,
        backLabel: "",
        headerTrailing: (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => agentRef.current?.openChatHistory?.()}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-600 active:bg-slate-100"
              aria-label="Chat history"
              title="Chat history"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                <circle cx="10" cy="4" r="1.5" />
                <circle cx="10" cy="10" r="1.5" />
                <circle cx="10" cy="16" r="1.5" />
              </svg>
            </button>
          </div>
        ),
        actionTray: null,
        hideShellHeader: false,
      });
    };
    apply();
    const t = window.setTimeout(apply, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [isActive, isMobile, panel, setChrome]);

  if (showAsk && panel !== "add") {
    const launchKey = askLaunch?.query
      ? `ask-${String(askLaunch.query).slice(0, 64)}`
      : "ask";
    return (
      <section
        className={v2cx(
          // Mobile: fill the shell main pane (no -my flush) so chat can scroll
          // inside the agent and bottom tabs stay in the flex layout.
          isMobile
            ? "flex h-full min-h-0 flex-1 flex-col overflow-hidden px-0 py-0"
            : v2cx(
                tokens.app.mainFlush,
                "flex min-h-0 flex-1 flex-col overflow-hidden px-3 pb-3 pt-2"
              )
        )}
      >
        {isDemo ? (
          <div className="shrink-0 border-b border-amber-200 bg-amber-50 px-3 py-2.5 sm:px-4">
            <p className="text-sm font-semibold text-amber-950">
              Sample: Customer Payments Hub — read-only demo
            </p>
            <p className="text-xs text-amber-900/80">
              Ask is grounded in this curated banking map — not the open web.
            </p>
            <div className="mt-2 flex flex-wrap gap-2 pb-1">
              <button
                type="button"
                onClick={() => setShowSoftGate(true)}
                className="inline-flex min-h-[40px] items-center rounded-lg bg-blue-700 px-3.5 text-sm font-semibold text-white"
              >
                Try the full loop
              </button>
              <button
                type="button"
                onClick={() => setShowHowBuilt(true)}
                className="inline-flex min-h-[40px] items-center rounded-lg border border-amber-300 bg-white px-3.5 text-sm font-medium text-amber-950"
              >
                How was this map built?
              </button>
            </div>
          </div>
        ) : null}

        <div
          className={v2cx(
            "flex min-h-0 flex-1 flex-col overflow-hidden",
            isMobile ? "" : tokens.surface.card
          )}
        >
          <EnhancedAIAgentV2
            key={launchKey}
            ref={agentRef}
            embedded
            isMobile={isMobile}
            showHowtoChips={!isDemo}
            demoMode={isDemo}
            trialOnboarding={!isDemo}
            pendingReviewCount={pendingReviewCount}
            initialQuery={askLaunch?.query || ""}
            initialEntity={askLaunch?.entity || null}
            guideHighlightSamplePack={
              Boolean(
                productGuide?.active && productGuide?.chapter?.id === "intent"
              )
            }
            guideLockSecondaryStarts={
              Boolean(
                productGuide?.active && productGuide?.chapter?.id === "intent"
              )
            }
            onHowtoNavigate={(msg) => {
              const raw = String(msg || "");
              // Guided demo: one track only — entity-first via beginWithSamplePack
              // (create CPH). Do not also run sample-pack deep link (would race).
              if (
                raw.includes("sample-pack") &&
                productGuide?.active &&
                typeof productGuide?.beginWithSamplePack === "function"
              ) {
                productGuide.beginWithSamplePack();
                return;
              }
              navigateHowto(onNavigate, V2_ROUTES, msg);
            }}
          />
        </div>

        {showHowBuilt ? (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" aria-label="How this map was built">
            <button type="button" className="absolute inset-0 bg-slate-900/50" aria-label="Close" onClick={() => setShowHowBuilt(false)} />
            <div className="relative w-full sm:max-w-lg max-h-[88vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white p-5 pb-8">
              <h3 className="text-lg font-semibold text-slate-900">How this map was built</h3>
              <p className="mt-1 text-sm text-slate-600">Content first — no “create entity” step before processing.</p>
              <ol className="mt-4 space-y-3">
                {HOW_BUILT_STEPS.map((s, i) => (
                  <li key={s.title} className="flex gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold">{i + 1}</span>
                    <span>
                      <span className="block text-sm font-semibold text-slate-900">{s.title}</span>
                      <span className="block text-sm text-slate-600">{s.body}</span>
                    </span>
                  </li>
                ))}
              </ol>
              <button
                type="button"
                onClick={() => { setShowHowBuilt(false); setShowSoftGate(true); }}
                className="mt-5 w-full rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white"
              >
                Try the full loop
              </button>
            </div>
          </div>
        ) : null}

        <SoftGateSheet
          open={showSoftGate}
          onClose={() => setShowSoftGate(false)}
          onAuthenticated={(u) => { setShowSoftGate(false); onGateAuthenticated?.(u); }}
          onNeedsGoogleSignup={(p) => { setShowSoftGate(false); onGateNeedsGoogle?.(p); }}
          onFullSignup={() => { setShowSoftGate(false); onGateFullSignup?.(); }}
        />
      </section>
    );
  }

  if (panel === "add" && !isMobile) {
    return (
      <section
        className={v2cx(
          tokens.app.mainFlush,
          "flex min-h-0 flex-1 flex-col overflow-hidden px-3 pb-3 pt-2"
        )}
      >
        <button
          type="button"
          onClick={handleBack}
          className={v2cx(
            "shrink-0 self-start inline-flex items-center gap-2 pb-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          )}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </button>

        <div className={`${tokens.surface.card} flex min-h-0 flex-1 flex-col overflow-hidden`}>
          <div className={v2cx(tokens.surface.panelHeader, "shrink-0 !py-2 sm:!py-2.5")}>
            <h2 className="text-base font-semibold text-slate-900">Add knowledge</h2>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto border-t border-slate-200 v2-explorer-light px-4 py-3 pb-6 sm:px-5 sm:py-4 sm:pb-8">
            <SystemInput
              key={`add-knowledge-${addPanelKey}`}
              onNavigateToGraph={
                onNavigate ? () => onNavigate(V2_ROUTES.explorer) : undefined
              }
              onNavigateToInbox={
                onNavigate ? () => onNavigate(V2_ROUTES.insightsInbox) : undefined
              }
            />
          </div>
        </div>
      </section>
    );
  }

  return null;
};

export default V2HomePage;
